<template>
  <div>
    <!-- Hero Slider - Lazy load with priority -->
    <LazyHeroSlider priority />
 
    <!-- Picked For You Section - Lazy load -->
    <LazyVisiblePickedForYou hydrate-on-visible class="sm:px-8 overflow-hidden"/>
    
    <div class="mx-auto px-4 py-8">
      <div class="mb-8">
            <!-- Model Filter Component - Removed -->
      </div>
    </div>

    <div class="container mx-auto px-4 py-8">
      <div class="mb-8">
        <!-- Static content loads immediately -->
        <div class="text-center mb-2">
          <p class="text-lg text-primary font-medium">{{ siteStore.name || '' }}</p>
        </div>
        
        <h1 class="text-4xl md:text-5xl font-bold mb-4 text-center">
          <span class="text-primary">Discover</span> our Range
        </h1>
        
        <!-- Main Front Search - Lazy loaded -->
        <div class="mb-8">
          <LazyVisibleFacetedSearchWidget hydrate-on-visible />
        </div>
        
        <!-- Vehicle Count Pills - Optimized with memo -->
        <ClientOnly>
          <div v-if="vehiclesStore.isDataLoaded" class="flex flex-wrap justify-center gap-4 mb-6">
            <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
              <span class="text-sm">Total Vehicles: <strong>{{ vehiclesStore.vehicleCount }}</strong></span>
            </div>
            <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
              <span class="text-sm">New Vehicles: <strong>{{ vehicleCounts.new }}</strong></span>
            </div>
            <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
              <span class="text-sm">Demo Vehicles: <strong>{{ vehicleCounts.demo }}</strong></span>
            </div>
            <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
              <span class="text-sm">Used Vehicles: <strong>{{ vehicleCounts.used }}</strong></span>
            </div>
          </div>
          
          <template #fallback>
            <div class="flex flex-wrap justify-center gap-4 mb-6">
              <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
                <span class="text-sm">Total Vehicles: <strong>Loading...</strong></span>
              </div>
              <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
                <span class="text-sm">New Vehicles: <strong>Loading...</strong></span>
              </div>
              <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
                <span class="text-sm">Demo Vehicles: <strong>Loading...</strong></span>
              </div>
              <div class="bg-card rounded-full px-4 py-2 shadow-xs border border-border">
                <span class="text-sm">Used Vehicles: <strong>Loading...</strong></span>
              </div>
            </div>
          </template>
        </ClientOnly>
        
        <!-- Vehicle count heading with ClientOnly wrapper -->
        <ClientOnly>
          <h2 v-if="vehiclesStore.isDataLoaded" class="text-2xl font-medium text-center mb-6">{{ vehiclesStore.vehicleCount }} vehicles to explore</h2>
          <template #fallback>
            <h2 class="text-2xl font-medium text-center mb-6">Vehicles to explore</h2>
          </template>
        </ClientOnly>
        
        <!-- Featured Sections - No hydration needed for static content -->
        <div class="grid md:grid-cols-2 gap-8 mb-16">
          <div class="bg-card rounded-lg shadow-sm p-6 border border-border">
            <h2 class="text-2xl font-semibold mb-4">Our Services</h2>
            <p class="text-muted-foreground mb-6">
              Discover our comprehensive range of transportation and automotive services.
              From sales to maintenance, we've got you covered.
            </p>
            <NuxtLink 
              to="/services" 
              class="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
            >
              Learn More
            </NuxtLink>
          </div>
          
          <div class="bg-card rounded-lg shadow-sm p-6 border border-border">
            <h2 class="text-2xl font-semibold mb-4">Browse Vehicles</h2>
            <p class="text-muted-foreground mb-6">
              Explore our extensive inventory of quality vehicles. Find the perfect match for your needs and budget.
            </p>
            <NuxtLink 
              :to="{ path: '/vehicles', query: currentFilterQuery }" 
              class="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
            >
              View Inventory
            </NuxtLink>
          </div>
        </div>
        
        <!-- Latest Vehicles - Lazy and cached -->
  
          <LazyVisibleLatestArrivals class="mb-16" hydrate-on-visible />
      
        
        <!-- Featured Vehicles List - Lazy and cached -->
        <div class="bg-background py-12 mb-16">

            <LazyVisibleFeaturedVehiclesDisplay hydrate-on-visible />
    
        </div>
        
        <!-- Featured Models - Lazy and cached with lower priority -->
        <LazyVisibleFeaturedModels class="mb-16" hydrate-on-visible />
        
        <!-- Footer Banner - Lazy and cached with lower priority -->
        <LazyVisibleFooterBanner class="mb-16" hydrate-on-visible />
        
        <!-- Contact Section - Static content, no lazy loading needed -->
        <div class="bg-card rounded-lg shadow-sm p-8 border border-border">
          <div class="grid md:grid-cols-2 gap-8">
            <div>
              <h2 class="text-2xl font-semibold mb-4">Get in Touch</h2>
              <p class="text-muted-foreground mb-6">
                Have questions or need assistance? Our team is here to help.
                Contact us today to learn more about our services.
              </p>
              <div class="space-y-4">
                <div v-if="siteStore?.phone" class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  <a :href="`tel:${siteStore.phone}`" class="hover:text-primary transition-colors">{{ siteStore.phone }}</a>
                </div>
                <div v-if="siteStore?.showroom_address" class="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary mt-1"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  <span>{{ siteStore.showroom_address }}</span>
                </div>
              </div>
              <NuxtLink 
                to="/contact" 
                class="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition mt-6"
              >
                Contact Us
              </NuxtLink>
            </div>
            <div class="hidden md:block">
              <!-- Placeholder for map or image - Lazy load iframe -->
              <div class="aspect-video w-full rounded-lg overflow-hidden bg-accent/20">
                <ClientOnly>
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d12540.462791353077!2d146.4989013!3d-38.2072105!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b29180aa76727f3%3A0x5c82340aceb19db!2sTraralgon%20Subaru!5e0!3m2!1sen!2sau!4v1683501448897!5m2!1sen!2sau" 
                    width="100%" 
                    height="100%" 
                    style="border:0;" 
                    :allowfullscreen="true" 
                    loading="lazy" 
                    referrerpolicy="no-referrer-when-downgrade">
                  </iframe>
                  <template #fallback>
                    <div class="w-full h-full flex items-center justify-center">
                      <span>Loading map...</span>
                    </div>
                  </template>
                </ClientOnly>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAgentStore } from '~/stores/agent'
import { useVehiclesStore } from '~/stores/vehicles'
import { useSiteStore } from '~/stores/site'
import { usePromotionalStore } from '~/stores/promotional'
import { useFrontPageStore } from '~/stores/frontPage'
import { computed, onMounted, watchEffect, shallowRef } from 'vue'
import type { VehicleResponse } from '~/types'

// Component imports
// import HomePageFilters from '~/components/FacetedSearchWidget.vue' // Should be commented
// import HeroSlider from '~/components/HeroSlider.vue' // TO BE COMMENTED
// import FooterBanner from '~/components/FooterBanner.vue' // TO BE COMMENTED
// import LatestArrivals from '~/components/LatestArrivals.vue' // TO BE COMMENTED
// import FeaturedVehiclesDisplay from '~/components/FeaturedVehiclesDisplay.vue' // TO BE COMMENTED
// import FeaturedModels from '~/components/FeaturedModels.vue' // TO BE COMMENTED
// import PickedForYou from '~/components/PickedForYou.vue' // TO BE COMMENTED

// Store initialization
const agentStore = useAgentStore()
const vehiclesStore = useVehiclesStore()
const siteStore = useSiteStore()
const promotionalStore = usePromotionalStore()
const frontPageStore = useFrontPageStore()

// Optimize vehicle counts with single computation
const vehicleCounts = computed(() => {
  // Only compute once instead of three separate computed properties
  let newCount = 0;
  let demoCount = 0;
  let usedCount = 0;
  
  // Single loop over vehicles - with safety check
  for (const vehicle of vehiclesStore?.vehicles || []) {
    const condition = vehicle.condition?.value?.[0]?.toLowerCase() || '';
    if (condition === 'new') newCount++;
    else if (condition === 'demo') demoCount++;
    else if (condition === 'used') usedCount++;
  }
  
  return {
    new: newCount,
    demo: demoCount,
    used: usedCount
  }
});

// Add vehicleCount getter to VehiclesStore to avoid repeated .length calls
// This assumes you'll add this getter to the store, or you can keep using vehiclesStore.vehicles.length

// --- Optimized Data Fetching using useAsyncData ---
const { data: vehicleDataResult, error: vehicleDataError } = useAsyncData(
  'vehicle-data',
  async () => {
    // Skip if data already loaded to prevent unnecessary work
    if (vehiclesStore.isDataLoaded && vehiclesStore.vehicles.length > 0) {
      vehiclesStore.isLoading = false;
      return { success: true, count: vehiclesStore.vehicles.length };
    }
    
    vehiclesStore.isLoading = true;
    
    try {
      // Fetch vehicles data
      const vehicleData = await $fetch<VehicleResponse>('/api/vehicles-new');
      
      if (vehicleData && vehicleData.vehiclesData) {
        // Process vehicles data with bulk operations
        vehiclesStore.vehicles = vehicleData.vehiclesData.map((vehicle: any) => ({
          ...vehicle,
          price: typeof vehicle.price === 'string' ? parseFloat(vehicle.price) : (vehicle.price ?? 0),
          kms: typeof vehicle.kms === 'string' ? parseFloat(vehicle.kms) : (vehicle.kms ?? 0),
          perweek: typeof vehicle.perweek === 'string' ? parseFloat(vehicle.perweek) : (vehicle.perweek ?? 0),
          photos: Array.isArray(vehicle.photos) ? vehicle.photos : [],
          make: vehicle.make ?? { value: [], displayValue: [] },
          model: vehicle.model ?? { value: [], displayValue: [], displayMake: '', displayBody: [] },
          condition: vehicle.condition ?? { value: [], displayValue: [] },
          body: vehicle.body ?? { value: [], displayValue: [] },
          transmission: vehicle.transmission ?? { value: [], displayValue: [] },
          fuel: vehicle.fuel ?? { value: [], displayValue: [] },
          year: vehicle.year ?? { value: [], displayValue: [] },
          address: vehicle.address ?? { line1: '', state: '', suburb: '', postcode: '' },
        }));
        
        vehiclesStore.isDataLoaded = true;
        vehiclesStore.isLoading = false;
        
        return {
          success: true,
          count: vehiclesStore.vehicles.length
        };
      } else {
        vehiclesStore.error = 'No vehicle data found';
        vehiclesStore.isLoading = false;
        return { success: false, count: 0 };
      }
    } catch (error) {
      vehiclesStore.error = 'Failed to load vehicles';
      vehiclesStore.isLoading = false;
      return { success: false, count: 0 };
    }
  },
  {
    server: true,
    lazy: false,
    default: () => ({ success: false, count: 0 })
  }
);

// --- Optimized Site Data Initialization ---
const { data: siteDataResult, error: siteDataError } = useAsyncData(
  'site-data',
  async () => {
    if (!vehicleDataResult.value?.success) return { success: false };
    
    try {
      await frontPageStore.initializeFrontPage();
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  },
  {
    server: true,
    lazy: false,
    default: () => ({ success: false })
  }
);

// Computed property for current filter query - optimized with shallow comparison
const currentFilterQuery = computed(() => {
  const query: Record<string, string | string[]> = {};
  
  // Add search query if present
  const searchFilter = vehiclesStore.activeFilters.search_keywords;
  if (searchFilter && typeof searchFilter === 'object' && 'value' in searchFilter) {
    if (typeof searchFilter.value === 'string' || Array.isArray(searchFilter.value)) {
      query.search_keywords = searchFilter.value;
    }
  }
  
  // Add other active filters - optimized to reduce iterations
  for (const [key, value] of Object.entries(vehiclesStore.activeFilters)) {
    if (!value || key === 'search_keywords') continue;
    
    if (typeof value === 'object' && 'type' in value) {
      if (value.type === 'array' && 'values' in value && Array.isArray(value.values) && value.values.length > 0) {
        query[key] = value.values;
      } else if (value.type === 'range' && 'min' in value && 'max' in value) {
        query[key] = `${value.min}-${value.max}`;
      } else if (value.type === 'text' && 'value' in value) {
        query[key] = value.value;
      }
    }
  }
  
  return query;
});

// Set page title
useSeoMeta({
  title: siteStore.name || 'Welcome to Our Website',
  description: siteStore.description || 'Your trusted partner in transportation and automotive solutions.'
});

// Initialize agent from URL - optimized with watchEffect cleanup
watchEffect((onCleanup) => {
  let isCancelled = false;
  
  if (vehiclesStore.isDataLoaded && vehiclesStore.vehicles.length > 0) {
    agentStore.initializeFromUrl()
      .catch(error => {
        if (!isCancelled) console.error('[pages/index] Error during agentStore.initializeFromUrl:', error);
      });
  }
  
  onCleanup(() => {
    isCancelled = true;
  });
});

// Define what we expect the store to have
// Note: Add this getter to your vehiclesStore
/*
// In stores/vehicles.ts
get vehicleCount(): number {
  return this.vehicles.length;
}
*/
</script>